import { PropNames } from "@showitinc/merge-utils";
import {
  FORM_FIELD_NESTED_INPUT_PROPS,
  getFormFieldStateProps,
  toStateProp,
} from "../../utils/Element/FormFieldPropertyMaps";
import { isStatePropCustomized } from "../../utils/FormFieldStateUtils";

const { CONTROL_BORDER_STYLE } = PropNames;

/**
 * Splits state props into those the entry customizes and those still inheriting, keeping order.
 * A pending prop (added in the panel, not yet edited) counts as customized.
 * @param {Object} params
 * @param {ImmutableMap} params.entry
 * @param {string} params.layout
 * @param {string[]} params.stateProps
 * @param {string[]} params.pending
 * @param {Object} params.metaMap
 * @returns {{ customized: string[], available: string[] }}
 */
export const partitionStateProps = ({ entry, layout, stateProps, pending, metaMap }) => {
  const customized = [];
  const available = [];
  for (const stateProp of stateProps) {
    const isCustomized =
      pending.includes(stateProp) || isStatePropCustomized(entry, layout, stateProp, metaMap);
    (isCustomized ? customized : available).push(stateProp);
  }
  return { customized, available };
};

/**
 * Lays out one section on Focus or Error: the props the entry customizes (or the panel has just
 * added) as rows under their group headings, and the rest as Customize menu items. Border colour
 * and width indent only when their group also shows the Border Style row they sit under. A menu
 * label that repeats within the section is prefixed with its group, e.g. `Arrow: Size`.
 * @param {Object} params
 * @param {{ title?: string, props: string[] }[]} params.groups - Base prop codes per group, already
 *   filtered to the ones the field can show.
 * @param {string} params.state - `FORM_STATE_FOCUS` or `FORM_STATE_ERROR`.
 * @param {ImmutableMap} params.entry - The `theme.styles` form entry the section edits.
 * @param {string} params.layout
 * @param {string[]} params.pending - State props added from the menu but not yet stored.
 * @param {Object} params.metaMap - Supplies each prop's label and a composite's children.
 * @returns {{
 *   groups: { title?: string, rows: { stateProp: string, nested: boolean }[] }[],
 *   menuItems: { stateProp: string, label: string }[],
 * }}
 */
export const buildStateSectionGroups = ({ groups, state, entry, layout, pending, metaMap }) => {
  const stateGroups = groups.map(({ title, props }) => ({
    title,
    stateProps: getFormFieldStateProps(props, state),
  }));
  const { customized, available } = partitionStateProps({
    entry,
    layout,
    stateProps: stateGroups.flatMap(({ stateProps }) => stateProps),
    pending,
    metaMap,
  });
  const borderStyle = toStateProp(CONTROL_BORDER_STYLE, state);
  const nestedProps = getFormFieldStateProps(FORM_FIELD_NESTED_INPUT_PROPS, state);

  const rowGroups = stateGroups
    .map(({ title, stateProps }) => {
      const shown = stateProps.filter((stateProp) => customized.includes(stateProp));
      const indents = shown.includes(borderStyle);
      return {
        title,
        rows: shown.map((stateProp) => ({
          stateProp,
          nested: indents && nestedProps.includes(stateProp),
        })),
      };
    })
    .filter(({ rows }) => rows.length);

  const offered = stateGroups.flatMap(({ title, stateProps }) =>
    stateProps
      .filter((stateProp) => available.includes(stateProp))
      .map((stateProp) => ({ stateProp, title, label: metaMap[stateProp].label })),
  );
  const isRepeated = (label) => offered.filter((item) => item.label === label).length > 1;

  return {
    groups: rowGroups,
    menuItems: offered.map(({ stateProp, title, label }) => ({
      stateProp,
      label: title && isRepeated(label) ? `${title}: ${label}` : label,
    })),
  };
};
